/**
 * Who is signed in, as an avatar in the bar and a small menu under it.
 *
 * THE NAME, THE ROLE AND SIGNING OUT USED TO BE THREE THINGS IN THE BAR, all the time, for
 * a question somebody asks about once a week: whose session is this. At 1100px the name
 * already gave way to the avatar, so the bar was two different designs either side of a
 * breakpoint. Now it is the avatar at every width, and everything about the account is one
 * press away - which is also the only place a new account action has to be added.
 *
 * ON PRESS, NOT ON HOVER. The same dashboard runs on a tablet at a counter, where there is
 * no hover, and a menu that opens as a pointer crosses the bar on its way to the clock is
 * one that opens when nobody asked. Hover only shows that the avatar can be pressed.
 *
 * ITEMS ARE DATA. A link or an action, each with an icon and a label, drawn in the order
 * given; `danger` sets signing out apart from the rest, because it is the one item that
 * ends something. The panel's dismissal rules are the `Listbox`'s: a press outside closes
 * it before it can land on whatever is underneath, and Escape returns focus to the avatar.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { cx } from "../ui";

export type MenuItem =
  | { kind: "link"; label: string; icon: ReactNode; to: string }
  | { kind: "action"; label: string; icon: ReactNode; onSelect: () => void; danger?: boolean };

export function UserMenu({
  avatar,
  name,
  detail,
  items,
}: {
  /** Drawn in the bar and again at the head of the panel. */
  avatar: ReactNode;
  /** The first line in the panel - a person's name, or their email when they have none. */
  name: string;
  /** The second line: the email under a name, or the role. */
  detail: string;
  items: MenuItem[];
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const entries = useRef<Array<HTMLElement | null>>([]);
  const id = useId();

  // Focus lands on the first item, so the arrow keys work from the moment it opens.
  useEffect(() => {
    if (open) entries.current[0]?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);

  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (!open) return;
    const at = entries.current.findIndex((node) => node === document.activeElement);
    const move = (to: number) => {
      event.preventDefault();
      entries.current[(to + items.length) % items.length]?.focus();
    };
    switch (event.key) {
      case "ArrowDown":
        return move(at + 1);
      case "ArrowUp":
        return move(at < 0 ? -1 : at - 1);
      case "Home":
        return move(0);
      case "End":
        return move(items.length - 1);
      case "Escape":
        event.preventDefault();
        return close();
      case "Tab":
        return setOpen(false);
    }
  };

  const row =
    "flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-left text-sm " +
    "[&_svg]:flex-none [&_svg]:text-ink-faint hover:bg-sunken focus-visible:bg-sunken";

  return (
    <div ref={root} className="relative" onKeyDown={onKeyDown}>
      <button
        ref={trigger}
        type="button"
        aria-label={`Account: ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? `${id}-menu` : undefined}
        onClick={() => setOpen((was) => !was)}
        className={cx(
          "grid size-10 place-items-center rounded-full transition-colors",
          open ? "bg-sunken" : "hover:bg-line-soft",
        )}
      >
        {avatar}
      </button>

      {open && (
        <div
          id={`${id}-menu`}
          // Right-aligned to the avatar, which is the last thing in the bar: a panel that
          // grew rightwards from it would open off the edge of the screen.
          className="absolute top-full right-0 z-20 mt-1.5 w-64 overflow-hidden rounded-md border border-line bg-paper"
        >
          <div className="flex items-center gap-3 border-b border-line-soft px-3.5 py-3">
            {avatar}
            <span className="grid min-w-0 leading-tight">
              <span className="truncate text-sm font-medium text-ink">{name}</span>
              <span className="truncate text-xs text-ink-faint">{detail}</span>
            </span>
          </div>

          <ul role="menu" aria-label="Account" className="p-1">
            {items.map((item, index) => (
              <li key={item.label} role="none">
                {item.kind === "link" ? (
                  <Link
                    ref={(node) => {
                      entries.current[index] = node;
                    }}
                    role="menuitem"
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className={cx(row, "text-ink")}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                ) : (
                  <button
                    ref={(node) => {
                      entries.current[index] = node;
                    }}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setOpen(false);
                      item.onSelect();
                    }}
                    className={cx(
                      row,
                      item.danger ? "text-alert [&_svg]:text-current" : "text-ink",
                    )}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

